/**
 * Où le chauffeur doit-il réellement se rendre, maintenant ?
 *
 * Un conteneur destiné à Koumassi peut être sorti du terminal puis déposé au parc, et n'en
 * repartir que plus tard. Ces deux trajets sont distincts, et le second n'a pas encore lieu
 * d'être annoncé : sur un dépôt parc, le chauffeur va AU PARC, pas chez le client.
 *
 * Les écrans lisaient pourtant `lieuLivr || lieuLivraison || adresseClient` sans jamais
 * regarder `typeLivraison`. Ils affichaient donc la destination finale d'un trajet qui
 * n'était pas celui du jour — une erreur qu'aucun message ne signale et que le chauffeur
 * ne peut pas deviner, puisque l'écran a l'air parfaitement normal.
 *
 * `parcDestination` existait déjà, jusque dans le DTO et les types : c'est son usage qui
 * manquait.
 */

export const DEPOT_PARC = 'DEPOT_PARC';

/** Rendu lorsqu'un dépôt parc ne porte aucun parc : voir {@link destinationMission}. */
export const PARC_MANQUANT = 'Parc non renseigné';

export const SANS_DESTINATION = '—';

export interface SourceDestination {
    typeLivraison?: string;
    parcDestination?: string;
    adresseLivraison?: string;
    adresseClient?: string;
    lieuLivr?: string;
    lieuLivraison?: string;
}

const premierRenseigne = (...valeurs: Array<string | undefined | null>): string | undefined =>
    valeurs.find((v) => typeof v === 'string' && v.trim() !== '')?.trim();

export const estDepotParc = (mission?: SourceDestination | null): boolean =>
    String(mission?.typeLivraison ?? '').trim().toUpperCase() === DEPOT_PARC;

/**
 * Destination à afficher au chauffeur.
 *
 * Sur un dépôt parc sans `parcDestination`, on rend {@link PARC_MANQUANT} plutôt que de
 * retomber sur l'adresse du client. Le repli paraîtrait plus aimable, mais il réintroduirait
 * exactement le défaut corrigé ici — en le rendant cette fois impossible à repérer, puisque
 * l'écran afficherait une adresse plausible. Mieux vaut une donnée manquante qui se voit
 * qu'une donnée fausse qui se cache : la première fait rappeler le commis, la seconde envoie
 * un camion à l'autre bout de la ville.
 */
export const destinationMission = (mission?: SourceDestination | null): string => {
    if (!mission) return SANS_DESTINATION;

    if (estDepotParc(mission)) {
        return premierRenseigne(mission.parcDestination) ?? PARC_MANQUANT;
    }

    // Ordre inchangé pour les livraisons directes et les sorties de parc : `adresseLivraison`
    // est la valeur calculée par le backend, les suivantes sont les replis historiques.
    return premierRenseigne(
        mission.adresseLivraison,
        mission.lieuLivr,
        mission.lieuLivraison,
        mission.adresseClient,
    ) ?? SANS_DESTINATION;
};

/** Intitulé du champ, pour que l'écran dise de quel genre de trajet il s'agit. */
export const libelleDestination = (mission?: SourceDestination | null): string =>
    estDepotParc(mission) ? 'DÉPÔT PARC' : 'DESTINATION';
